import { useInlineRename } from '@common/Copilot/CopilotAgentChat/agent-v4/InlineRename';
import { MessageSquareAIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Ellipsis } from '@prophecy/ui-v3/Ellipsis';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Text } from '@prophecy/ui-v3/Typography';
import styled from 'styled-components';

import { ChatActionsMenu } from './ChatActionsMenu';
import { ChatTitleInput } from './ChatTitleInput';
import { HOME_CHAT_TEST_IDS, NEW_CHAT_LABEL, NEW_CONVERSATION_NAME } from './constants';

import type { HomeConversation } from './api';

// The new-chat state lets the page backdrop show through; a started chat gets a white bar.
const Bar = styled(Stack)<{ $started: boolean }>`
  width: 100%;
  min-height: 52px;
  padding: 0 ${theme.spaces.x20};
  background: ${({ $started }) => ($started ? theme.colors.white : 'transparent')};
  border-bottom: 1px solid ${theme.colors.gray200};
`;

const Title = styled(Stack)`
  min-width: 0;
`;

function StartedChatHeader({
  conversation,
  pinned,
  renaming,
  deleting,
  onRename,
  onTogglePin,
  onDelete
}: {
  conversation: HomeConversation;
  pinned: boolean;
  renaming: boolean;
  deleting: boolean;
  onRename: (conversationId: string, name: string) => Promise<void>;
  onTogglePin: (conversationId: string) => void;
  onDelete: (conversationId: string) => Promise<void>;
}) {
  const name = conversation.name || NEW_CONVERSATION_NAME;
  const rename = useInlineRename({
    currentName: name,
    onRename: (nextName) => void onRename(conversation.conversationId, nextName),
    disabled: renaming || deleting
  });

  return (
    <>
      <Title direction='horizontal' alignY='center' gap={theme.spaces.x8}>
        <MessageSquareAIcon type='default' size='xs' color={theme.colors.gray700} />
        {rename.isEditing ? (
          <ChatTitleInput
            value={rename.value}
            weight={theme.fontWeight.semiBold}
            tone={theme.colors.gray700}
            onChange={rename.setValue}
            onSubmit={rename.submit}
            onKeyDown={rename.onKeyDown}
            data-test-id={HOME_CHAT_TEST_IDS.headerRenameInput}
          />
        ) : (
          <Ellipsis tooltip>
            <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.gray700}>
              {name}
            </Text>
          </Ellipsis>
        )}
      </Title>
      <ChatActionsMenu
        name={name}
        pinned={pinned}
        iconSize={theme.sizes.x14}
        renaming={renaming}
        deleting={deleting}
        onRename={rename.start}
        onTogglePin={() => onTogglePin(conversation.conversationId)}
        onDelete={() => onDelete(conversation.conversationId)}
      />
    </>
  );
}

export function ChatHeader({
  conversation,
  started,
  pinned,
  renamingId,
  deletingId,
  onRename,
  onTogglePin,
  onDelete
}: {
  conversation: HomeConversation | undefined;
  started: boolean;
  pinned: boolean;
  renamingId?: string;
  deletingId?: string;
  onRename: (conversationId: string, name: string) => Promise<void>;
  onTogglePin: (conversationId: string) => void;
  onDelete: (conversationId: string) => Promise<void>;
}) {
  return (
    <Bar
      $started={started}
      direction='horizontal'
      alignY='center'
      gap={theme.spaces.x12}
      data-test-id={HOME_CHAT_TEST_IDS.chatHeader}>
      {conversation ? (
        <StartedChatHeader
          key={conversation.conversationId}
          conversation={conversation}
          pinned={pinned}
          renaming={renamingId === conversation.conversationId}
          deleting={deletingId === conversation.conversationId}
          onRename={onRename}
          onTogglePin={onTogglePin}
          onDelete={onDelete}
        />
      ) : (
        <Title direction='horizontal' alignY='center' gap={theme.spaces.x8}>
          <MessageSquareAIcon type='default' size='xs' color={theme.colors.gray700} />
          <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.gray700}>
            {NEW_CHAT_LABEL}
          </Text>
        </Title>
      )}
    </Bar>
  );
}
